// alpha-score.js — Alpha Ledger scoring engine (Decision Loop, spec v0.1 + mandate model 2026-07-21)
//
// PURE functions only: no S import, no storage, no side effects. Everything is derived
// on demand from ledger events + an injected ctx snapshot; NOTHING is written anywhere
// (Supabase zero-bloat is a hard program constraint). English-only strings.
//
// ctx = { holdings, portfolios, trades, prices, fxRate }
//   holdings:   [{ ticker, currency, portfolioId }]
//   portfolios: [{ id, name, isDefault, mandate? }]   mandate: 'accumulate' | 'active'
//   trades:     [{ ticker, currency, date, status, closedAt? }]
//   prices:     { TICKER: { price } }   fxRate: USD/TRY
//
// Mandate model (locked 2026-07-21): a rec is scoreable only when it fits the owner's
// mandate on that asset. Resolution is per-POSITION at emit time:
//   1. open trade on the ticker at emit ts  -> 'active'  (Trades lane)
//   2. else holding's portfolio mandate     -> portfolio.mandate, defaulting to
//      'accumulate' for the default (Retirement) portfolio, 'active' otherwise
//   3. unknown ticker                       -> 'active' (market-level; usually non-directive)

export const LONG_TYPES = ['buy', 'scale_in', 'dca_adjust'];
export const EXIT_TYPES = ['sell', 'take_profit', 'stop_loss', 'hedge'];
export const DIRECTIVE_TYPES = [...LONG_TYPES, ...EXIT_TYPES]; // rebalance = v0.2 (pair legs)
const CLOSE_EVENTS = ['rec_superseded', 'rec_expired', 'rec_dismissed', 'rec_acted'];
export const UNIT_NOTIONAL = 1000; // $1k equal-weight per rec — scores advice quality, not sizing

export function mandateOfPortfolio(p) {
  if (!p) return 'active';
  if (p.mandate === 'accumulate' || p.mandate === 'active') return p.mandate;
  return p.isDefault ? 'accumulate' : 'active';
}

export function classifyMandate(ticker, ts, ctx) {
  if (!ticker) return 'active';
  const at = new Date(ts).getTime();
  // Real trade schema uses openDate/closeDate (legacy fallbacks kept). A Closed trade
  // WITHOUT a close date is NOT provably open at ts — it falls through to the holding
  // mandate (live bug 2026-07-21: closed XAU trade kept the ticker permanently 'active').
  const openAt = (ctx.trades || []).some(t => {
    if (t.ticker !== ticker) return false;
    const openTs = new Date(t.openDate || t.date || 0).getTime();
    if (!Number.isFinite(openTs) || openTs > at) return false;
    if (t.status !== 'Closed') return true;
    const closeTs = t.closeDate || t.closedAt;
    return closeTs ? new Date(closeTs).getTime() >= at : false;
  });
  if (openAt) return 'active';
  const h = (ctx.holdings || []).find(x => x.ticker === ticker);
  if (!h) return 'active';
  const p = (ctx.portfolios || []).find(x => x.id === h.portfolioId);
  return mandateOfPortfolio(p);
}

function ccyOf(ticker, ctx) {
  const h = (ctx.holdings || []).find(x => x.ticker === ticker);
  if (h && h.currency) return h.currency;
  const t = (ctx.trades || []).find(x => x.ticker === ticker);
  if (t) return t.currency || 'USD';
  return null; // unknown — excluded from scoring (never guessed)
}

/** First emit + first close event per rec_id. */
export function pairLifecycles(events) {
  const pairs = new Map();
  for (const e of events || []) {
    if (e.event === 'rec_emitted') {
      if (!pairs.has(e.rec_id)) pairs.set(e.rec_id, { emit: e, close: null });
      else if (!pairs.get(e.rec_id).emit) pairs.get(e.rec_id).emit = e;
    } else if (CLOSE_EVENTS.includes(e.event)) {
      if (!pairs.has(e.rec_id)) pairs.set(e.rec_id, { emit: null, close: e });
      else if (!pairs.get(e.rec_id).close) pairs.get(e.rec_id).close = e;
    }
  }
  return pairs;
}

/** USD price of one leg. TRY legs convert at that leg's own fx (currency integrity). */
function legUSD(price, fx, ccy) {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (ccy === 'TRY') return Number.isFinite(fx) && fx > 0 ? price / fx : null;
  return price; // USD/EUR-quoted: EUR holdings carry EUR prices ≈ scored in quote ccy vs USD — v0.1 treats non-TRY as USD-equivalent
}

/**
 * Score one lifecycle. Returns a row or { excluded: reason }.
 * alpha = counterfactual USD P&L of following the rec with UNIT_NOTIONAL at emit.
 */
export function scoreRec(pair, ctx) {
  const em = pair.emit;
  if (!em) return { excluded: 'no_emit' };
  if (!DIRECTIVE_TYPES.includes(em.rec_type)) {
    return { excluded: em.rec_type === 'rebalance' ? 'rebalance' : 'non_directive' };
  }
  const mandate = classifyMandate(em.ticker, em.ts, ctx);
  if (mandate !== 'active') return { excluded: 'mandate_filtered', ticker: em.ticker };
  const ccy = ccyOf(em.ticker, ctx);
  if (!ccy) return { excluded: 'unknown_ccy', ticker: em.ticker };
  const entry = legUSD(em.price_at, em.fx_at, ccy);
  if (entry == null) return { excluded: 'no_price', ticker: em.ticker };
  let exit = null, resolved = false, closedTs = null;
  const cl = pair.close;
  if (cl) {
    exit = legUSD(cl.price_at, cl.fx_at, ccy);
    if (exit != null) { resolved = true; closedTs = cl.ts; }
  }
  if (exit == null) {
    const p = (ctx.prices || {})[em.ticker];
    exit = legUSD(p && p.price, ctx.fxRate, ccy);
  }
  if (exit == null) return { excluded: 'no_exit_price', ticker: em.ticker };
  const dir = LONG_TYPES.includes(em.rec_type) ? 1 : -1;
  const alpha = dir * (exit / entry - 1) * UNIT_NOTIONAL;
  const lifeDays = ((closedTs ? new Date(closedTs) : new Date()) - new Date(em.ts)) / 86400000;
  return {
    recId: em.rec_id, ticker: em.ticker, type: em.rec_type, urgency: em.urgency || null,
    resolved, lifeDays: +lifeDays.toFixed(2), alpha: +alpha.toFixed(2),
    reasoning: em.reasoning || null,
    // Followed-vs-Ignored view: how the lifecycle closed + the captured note/reason
    closeEvent: cl ? cl.event : null, closeNote: (cl && cl.reasoning) || null,
  };
}

/** Score a full event set. Returns { rows, coverage }. */
export function scoreLedger(events, ctx) {
  const pairs = pairLifecycles(events);
  const rows = [], coverage = { emitted: 0, scored: 0, mandate_filtered: 0, rebalance: 0, non_directive: 0, no_price: 0, unknown_ccy: 0, acted: 0, dismissed: 0 };
  for (const e of events || []) {
    if (e.event === 'rec_emitted') coverage.emitted++;
    else if (e.event === 'rec_acted') coverage.acted++;
    else if (e.event === 'rec_dismissed') coverage.dismissed++;
  }
  for (const pair of pairs.values()) {
    const r = scoreRec(pair, ctx);
    if (r.excluded) {
      if (r.excluded === 'mandate_filtered') coverage.mandate_filtered++;
      else if (r.excluded === 'rebalance') coverage.rebalance++;
      else if (r.excluded === 'non_directive') coverage.non_directive++;
      else if (r.excluded === 'no_price' || r.excluded === 'no_exit_price') coverage.no_price++;
      else if (r.excluded === 'unknown_ccy') coverage.unknown_ccy++;
      continue;
    }
    rows.push(r);
    coverage.scored++;
  }
  return { rows, coverage };
}

/** Aggregate scored rows for the report card. */
export function aggregateScores(rows) {
  const byType = {};
  let total = 0, hits = 0, resolved = 0, resolvedHits = 0;
  const lives = [];
  for (const r of rows) {
    const a = byType[r.type] = byType[r.type] || { n: 0, resolved: 0, hits: 0, total: 0 };
    a.n++; a.total += r.alpha;
    if (r.alpha > 0) { a.hits++; hits++; }
    if (r.resolved) {
      a.resolved++; resolved++; lives.push(r.lifeDays);
      // A "hit rate" over rows that include OPEN theses marked to live price is a
      // mark-to-market snapshot, not an outcome record — it moves with today's prices and
      // can never settle. Track the resolved-only rate separately and let the UI lead with
      // it, because that is what the words "hit rate" promise. (audit 2026-08-01)
      if (r.alpha > 0) resolvedHits++;
    }
    total += r.alpha;
  }
  for (const a of Object.values(byType)) {
    a.hitPct = a.n ? Math.round(a.hits / a.n * 100) : 0;
    a.avg = a.n ? +(a.total / a.n).toFixed(1) : 0;
    a.total = +a.total.toFixed(0);
  }
  lives.sort((x, y) => x - y);
  const sorted = rows.slice().sort((x, y) => y.alpha - x.alpha);
  const totalLegs = rows.reduce((s, r) => s + (r.legs || 1), 0);
  return {
    n: rows.length, resolved, open: rows.length - resolved,
    total: +total.toFixed(0),
    // hitPct keeps its original meaning (all scored rows, incl. open marks) for anything
    // already reading it. hitPctResolved is the one to show.
    hitPct: rows.length ? Math.round(hits / rows.length * 100) : 0,
    hitPctResolved: resolved ? Math.round(resolvedHits / resolved * 100) : null,
    resolvedHits,
    medianLifeDays: lives.length ? +lives[Math.floor(lives.length / 2)].toFixed(2) : null,
    avgLegs: rows.length ? +(totalLegs / rows.length).toFixed(1) : null, // >1 only in chain mode
    byType,
    best: sorted[0] || null,
    worst: sorted[sorted.length - 1] || null,
  };
}

/**
 * CHAIN SCORING (v0.2, 2026-07-22). Scans supersede recs within hours (median leg life
 * 0.23d), shredding one thesis into many tiny scoring windows. The ledger records the
 * links explicitly: a rec_superseded row carries superseded_by = successor rec id.
 * A CHAIN follows those links while ticker AND direction class stay the same —
 * a direction flip is a new thesis. Scored as ONE position: entry at the FIRST leg's
 * emit, exit at the TERMINAL leg's close (or live mark), mandate at first emit,
 * type attributed to the first call.
 */
export function buildChains(events) {
  const pairs = pairLifecycles(events);
  const dirOf = t => LONG_TYPES.includes(t) ? 1 : EXIT_TYPES.includes(t) ? -1 : 0;
  // A supersession link is VALID only when the successor exists with an emit, same ticker
  // and same direction class. Only VALID targets stop being chain heads — a direction-flip
  // successor starts its OWN thesis (first test iteration missed this: the flipped rec
  // vanished from both the old chain and the head list).
  const _linkTarget = p => {
    if (!p.emit || !p.close || p.close.event !== 'rec_superseded' || !p.close.superseded_by) return null;
    const next = pairs.get(p.close.superseded_by);
    if (!next || !next.emit) return null;
    if (next.emit.ticker !== p.emit.ticker) return null;
    const d = dirOf(p.emit.rec_type);
    if (d === 0 || dirOf(next.emit.rec_type) !== d) return null;
    return p.close.superseded_by;
  };
  const validSuccessors = new Set();
  for (const p of pairs.values()) { const t = _linkTarget(p); if (t) validSuccessors.add(t); }
  const chains = [];
  for (const [recId, p] of pairs.entries()) {
    if (!p.emit || validSuccessors.has(recId)) continue; // walk only from chain heads
    const legs = [p];
    const visited = new Set([recId]);
    let cur = p;
    for (;;) {
      const nextId = _linkTarget(cur);
      if (!nextId || visited.has(nextId)) break;
      visited.add(nextId);
      const next = pairs.get(nextId);
      legs.push(next);
      cur = next;
    }
    chains.push(legs);
  }
  return chains;
}

/** Score a full event set as CHAINS. Same row/coverage interface as scoreLedger + legs stats. */
export function scoreChains(events, ctx) {
  const chains = buildChains(events);
  const rows = [], coverage = { emitted: 0, scored: 0, mandate_filtered: 0, rebalance: 0, non_directive: 0, no_price: 0, unknown_ccy: 0, acted: 0, dismissed: 0, chained_legs: 0 };
  for (const e of events || []) {
    if (e.event === 'rec_emitted') coverage.emitted++;
    else if (e.event === 'rec_acted') coverage.acted++;
    else if (e.event === 'rec_dismissed') coverage.dismissed++;
  }
  for (const legs of chains) {
    const head = legs[0], tail = legs[legs.length - 1];
    // synthetic pair: head's emit + tail's close — scoreRec does mandate/ccy/price work
    const r = scoreRec({ emit: head.emit, close: tail.close }, ctx);
    if (r.excluded) {
      if (r.excluded === 'mandate_filtered') coverage.mandate_filtered++;
      else if (r.excluded === 'rebalance') coverage.rebalance++;
      else if (r.excluded === 'non_directive') coverage.non_directive++;
      else if (r.excluded === 'no_price' || r.excluded === 'no_exit_price') coverage.no_price++;
      else if (r.excluded === 'unknown_ccy') coverage.unknown_ccy++;
      continue;
    }
    r.legs = legs.length;
    rows.push(r);
    coverage.scored++;
    coverage.chained_legs += legs.length;
  }
  return { rows, coverage };
}

/**
 * Mandate policy at the SOURCE (locked 2026-07-21): exit-side directives against
 * accumulate-mandate assets are policy-noise (selling is forbidden there). Instead of
 * suppressing the signal, transform it into mandate-consistent DCA guidance: an
 * informational 'alert' (non-directive → never scored) that suggests steering this
 * month's DCA rather than exiting. Original type preserved in metrics.transformedFrom.
 * Accumulation-side types (buy/scale_in/dca_adjust) pass through on any mandate.
 */
export function transformForMandate(rec, ctx) {
  if (!rec || !EXIT_TYPES.includes(rec.type)) return rec;
  const ts = rec.createdAt || new Date().toISOString();
  if (classifyMandate(rec.ticker, ts, ctx) !== 'accumulate') return rec;
  return {
    ...rec,
    type: 'alert',
    urgency: rec.urgency === 'high' ? 'medium' : 'low',
    title: `DCA guidance — ${rec.title || rec.ticker}`,
    action: `Accumulate mandate on ${rec.ticker}: no exits. Consider steering this month's DCA toward underweight components instead.`,
    reasoning: `${rec.reasoning || ''} [Engine signal was "${rec.type}" — transformed: this asset is on a 5-10y monthly accumulation plan.]`.trim(),
    metrics: { ...(rec.metrics || {}), mandate: 'accumulate', transformedFrom: rec.type },
  };
}

/** FI-days twin: alpha priced in days of essential spend (SSOT daily figure injected). */
export function fiDays(alphaUSD, dailyEssentialUSD) {
  if (!Number.isFinite(dailyEssentialUSD) || dailyEssentialUSD <= 0) return null;
  return +(alphaUSD / dailyEssentialUSD).toFixed(1);
}
